<template>
    <NConfigProvider
        :locale="customizedLocale"
        :date-locale="dateKoKR"
        :theme-overrides="themeOverrides"
    >
        <main
            class="main-container"
            :class="{
                'has-navbar-sidebar':
                    needNavbarAndSidebarPages.includes(currentRouteName),
                'has-navbar': needNavBarPages.includes(currentRouteName),
                'has-navbar-terms':
                    needNavbarAndTermsPages.includes(currentRouteName),
            }"
        >
            <Spinner
                :isLoading="global.usingSpinner && global.isLoading"
                :message="global.message"
            />

            <router-view name="NavBar"></router-view>
            <router-view name="SideBar"></router-view>

            <!-- PDS 스타일 페이지 -->
            <PdsPageWrapper v-if="isPdsStylePage">
                <router-view></router-view>
                <PdsFooter />
            </PdsPageWrapper>

            <!-- 기존 페이지 -->
            <div v-else class="page-wrap">
                <router-view class="page"></router-view>

                <div
                    v-if="
                        needNavbarAndSidebarPages.includes(currentRouteName) ||
                        needNavbarAndTermsPages.includes(currentRouteName)
                    "
                    class="page terms"
                >
                    <div class="logo">
                        <LogoSvg />
                    </div>
                    <div class="terms__links">
                        <a :href="`${TERMS_URL}#TERMS`" target="_blank"
                            >이용약관</a
                        >
                        <span class="separator"></span>
                        <a
                            :href="`${TERMS_URL}#PRIVACY_POLICY`"
                            target="_blank"
                            ><span class="strong">개인정보처리방침</span></a
                        >
                    </div>
                    <div class="terms__caption">
                        <p>
                            <span class="strong">여기어때 고객행복센터</span>
                            <span>1670-6250</span>
                        </p>
                        <p>
                            문의 시 '비즈니스회원'이라고 말씀주시면 더욱 원활한
                            상담이 가능합니다.
                        </p>
                        <p>
                            (주)여기어때컴퍼니 | Copyright © GC COMPANY Corp.
                            All rights reserved.
                        </p>
                    </div>
                </div>
            </div>

            <BaseAlert
                :is-open="globalAlertOpened"
                :title="globalAlertTitle"
                :message="globalAlertMessage"
                @close="closeAlert"
            />
            <BaseConfirm
                :is-open="globalConfirmOpened"
                :title="globalConfirmTitle"
                :message="globalConfirmMessage"
                :confirm-text="globalConfirmAcceptBtnName"
                :cancel-text="globalConfirmDenyBtnName"
                @accept="acceptConfirm"
                @cancel="closeConfirm"
            />
            <BaseToast
                :is-visible="globalToastVisible"
                :message="globalToastMessage"
                @after="afterToast"
                @close="closeToast"
            />
        </main>
    </NConfigProvider>
</template>

<script setup lang="ts">
import Spinner from '@/components/common/layout/Spinner.vue';
import BaseAlert from '@/components/common/popup/BaseAlert.vue';
import BaseConfirm from '@/components/common/popup/BaseConfirm.vue';
import BaseToast from '@/components/common/popup/BaseToast.vue';
import useMessage from '@/hooks/useMessage';
import useGlobalStore from '@/store/global-store';
import {
    GlobalThemeOverrides,
    NConfigProvider,
    dateKoKR,
    koKR,
    createLocale,
} from 'naive-ui';
import {computed} from 'vue';
import {useRoute} from 'vue-router';
import LogoSvg from '@/assets/image/logo.svg';
import PdsFooter from '@/components/pds/layout/Footer/Footer.vue';
import PdsPageWrapper from '@/components/pds/layout/PageWrapper/PageWrapper.vue';
import {
    BUSINESS_TRIP_ANALYSIS,
    COST_REDUCTION_ANALYSIS,
} from '@/types/statistics/constants';

const TERMS_URL = import.meta.env.VITE_APP_YEOGI_WEB_TERMS_URL;

const global = useGlobalStore();
const route = useRoute();
const currentRouteName = computed(() => route.name?.toString() ?? '');
const {
    closeAlert,
    acceptConfirm,
    closeConfirm,
    closeToast,
    afterToast,
    globalAlertOpened,
    globalAlertTitle,
    globalAlertMessage,
    globalConfirmOpened,
    globalConfirmTitle,
    globalConfirmMessage,
    globalConfirmAcceptBtnName,
    globalConfirmDenyBtnName,
    globalToastVisible,
    globalToastMessage,
} = useMessage();

const needNavBarPages = ['Register'];

const needNavbarAndSidebarPages = [
    'Reservation',
    'PointRequestHistory',
    'PointTargetList',
    'Commission',
    COST_REDUCTION_ANALYSIS,
    BUSINESS_TRIP_ANALYSIS,
    'CompanyInfo',
    'MemberList',
    'MemberIndividualRegister',
    'MemberBulkRegister',
    'MemberInvitation',
    'MemberDepartment',
    'UserInfo',
    'PointContractInfo',
    'BizPointSettlements',
    'SettlementHistory',
    'PointUsageHistory',
    'BusinessTripPolicy',
];
const needNavbarAndTermsPages = ['ThirdPartyAgreement'];

/** PDS 스타일이 적용된 페이지 (업무 예약 내역·포인트 사용 내역·출장 관련) */
const pdsStylePages = [
    'Reservation',
    'PointUsageHistory',
    BUSINESS_TRIP_ANALYSIS,
    'BusinessTripPolicy',
];

const isPdsStylePage = computed(() =>
    pdsStylePages.includes(currentRouteName.value),
);

const customizedLocale = createLocale(
    {
        DatePicker: {
            confirm: '선택',
        },
    },
    koKR,
);

/**
 * @desc naive-ui custom theme
 * @see https://www.naiveui.com/en-US/light/docs/customize-theme
 */
const themeOverrides: GlobalThemeOverrides = {
    common: {
        primaryColor: '#5694f0',
        primaryColorHover: '#6fa4f2',
        primaryColorPressed: '#497ecc',

        infoColor: '#49627a',
        infoColorHover: '#647a8e',
        infoColorPressed: '#3e5368',

        successColor: '#32a842',
        successColorHover: '#51b55e',
        successColorPressed: '#2b8f38',

        warningColor: '#ffc107',
        warningColorHover: '#ffca2c',
        warningColorPressed: '#d9a406',

        errorColor: '#ff5c5c',
        errorColorHover: '#ff7474',
        errorColorPressed: '#d94e4e',

        fontWeightStrong: 'bold',
    },
    Button: {
        borderRadiusLarge: '6px',
        borderRadiusMedium: '6px',
        borderRadiusSmall: '6px',
    },
    Checkbox: {
        colorDisabled: '#e9e9e9',
    },
    DatePicker: {
        peers: {
            Input: {
                heightLarge: '38px',
                paddingLarge: '0',
                border: '1px solid rgba(0, 0, 0, 0.16)',
                borderRadius: '6px',
            },
        },
    },
    DataTable: {
        thColor: '#f8f8fa',
        thTextColor: '#474747',
        tdTextColor: '#333',
        tdPaddingMedium: '10px',
        borderColor: '#e5e5e5',
        paginationMargin: '25px 0 0 0',
        peers: {
            Pagination: {
                itemSizeMedium: '40px',
                itemTextColorPressed: '#497ecc',
            },
        },
    },
    Tabs: {
        tabFontWeight: 'bold',
        tabFontWeightActive: 'bold',
    },
    Tooltip: {
        color: 'rgba(38, 38, 38, 0.9)',
        borderRadius: '6px',
    },
};
</script>

<style lang="scss" scoped>
.main-container {
    &.has-navbar-sidebar {
        margin-top: 56px;
        padding-left: 200px;
        height: calc(100vh - 56px); // NOTE: NavBar height

        .page-wrap {
            height: calc(100% - 32px); // NOTE: padding top + bottom
            min-height: calc(100vh - 56px - 32px);
            overflow-y: auto;
            padding: 0 16px 16px;
        }

        .page {
            background-color: $white;
            border-radius: 6px;
        }

        .terms {
            padding: 20px 24px 72px;

            @media (max-width: 500px) {
                padding: 16px;
            }
            .logo {
                padding-bottom: 2px;
                margin-bottom: 12px;
            }

            &__links {
                color: $NDS44;
                font-size: 12px;
                font-style: normal;
                font-weight: 600;
                line-height: 14px;
                display: flex;
                align-items: center;
                gap: 8px;
                .strong {
                    color: $NDS20;
                }
                .separator {
                    width: 1px;
                    height: 8px;
                    display: inline-block;
                    background: $black_08;
                }
            }

            &__caption {
                margin-top: 16px;
                color: $NDS60;
                font-size: 12px;
                line-height: 16px;
                font-weight: 400;
                display: flex;
                flex-direction: column;
                gap: 4px;
                .strong {
                    font-weight: 600;
                    margin-right: 4px;
                }

                p {
                    &.copyright {
                        margin-top: 4px;
                        font-size: 10px;
                        font-weight: 400;
                        line-height: 12px;
                        color: $NDS68;
                    }
                }
            }
        }
    }

    &.has-navbar {
        margin-top: 56px;
        padding: 56px 0 0 0;
        min-height: calc(100vh - 56px); // NOTE: NavBar height
        height: 100%;
        background-color: $white;
    }

    &.has-navbar-terms {
        margin-top: 56px;
        min-height: calc(100vh - 56px); // NOTE: NavBar height

        .page-wrap {
            height: calc(100% - 32px); // NOTE: padding top + bottom
            min-height: calc(100vh - 56px - 32px);
            overflow-y: auto;
            padding: 16px;
        }

        .page {
            background-color: $white;
            border-radius: 6px;
        }

        .terms {
            margin-top: 16px;
            padding: 20px 24px;

            &__links {
                color: #354052;
                font-size: 12px;
                margin-bottom: 4px;

                .separator {
                    margin: 0 4px;
                }
            }

            &__caption {
                color: $black_32;
                font-size: 12px;

                p {
                    margin-bottom: 4px;
                }
            }
        }
    }
}
</style>
